import { journeyStore } from "@/app/services/journey";
import { PartnerType } from "@/app/utils/partners.enum";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cepService } from "@/app/services/cepService";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { useOccupations } from "@/app/hooks/useOccupations";
import { useCrefazMutations } from "@/app/hooks/useCrefazMutations";
import { useAuth } from "@/app/hooks/useAuth";
import { useCrefazContext } from "@/app/contexts/CrefazContext";
import { useToast } from "@/view/components/ui/use-toast";
import { validateCpf } from "@/app/utils/cpfFormater";
import { formatDateForInput } from "@/app/utils/formatDate";
import { OptionType } from "@/app/utils/optionSelectType";
import { crefazService } from "@/app/services/crefazService";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext"

const getGenerateProposalSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF é obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  name: z.string().min(1, "Nome é obrigatório"),
  birthDate: z.string().min(1, "Data de nascimento é obrigatória"),
  phone: z.string().optional(),
  ocupation: z.string().min(1, "Ocupação é obrigatória"),
  zipcode: z.string().min(1, "O campo CEP é obrigatório").optional(),
  city: z.string().optional(),
});

type getGenerateProposalFormData = z.infer<typeof getGenerateProposalSchema>;

interface UseGenerateProposalModalProps {
  onContinue?: () => void;
}

export default function useGenerateProposalModal({
  onContinue,
}: UseGenerateProposalModalProps) {
  const getGenerateProposalForm = useForm<getGenerateProposalFormData>({
    resolver: zodResolver(getGenerateProposalSchema),
    mode: "onChange",
  });

  const { watch, setValue, handleSubmit } = getGenerateProposalForm;

  const { toast } = useToast();

  const cpf = watch("cpf")?.replace(/\D/g, "");
  const city = watch("city");

  const { data, isSuccess } = useCustomerCpf(cpf);
  const { data: occupationsData } = useOccupations();
  const { makeProposalMutation } = useCrefazMutations();
  const { user } = useAuth();

  const { setCustomerData, setProposalData } = useCrefazContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [pin, setPin] = useState(["", "", "", ""]);
  const [cepWarning, setCepWarning] = useState("");

  const [selected, setSelected] = useState<"cep" | "city">("cep");
  const zipcode = watch("zipcode");
  const [isCityDisabled, setIsCityDisabled] = useState(true);
  const [cityOptions, setCityOptions] = useState<OptionType[]>([]);
  const [cityInput, setCityInput] = useState("");

  const debouncedCityInput = useDebounce(500, cityInput);

  useEffect(() => {
    if (city) {
      setCepWarning("");
    }
  }, [city]);

  useEffect(() => {
    const fetchCep = async () => {
      const cleanCep = zipcode?.replace(/\D/g, "");

      if (!cleanCep || cleanCep.length !== 8) {
        setIsCityDisabled(true);
        setValue("city", "");
        setCityOptions([]);
        setCepWarning("");
        return;
      }

      try {
        const response = await cepService(cleanCep);

        if (response.erro || !response.localidade || !response.uf) {
          setIsCityDisabled(false);
          setValue("city", "");
          setCityOptions([]);
          setCepWarning(
            "CEP inválido - informe nome da cidade ou UF para pesquisa",
          );
        } else {
          const cityUf = `${response.localidade} - ${response.uf}`;

          setSelected("city");
          setValue("city", cityUf);
          setCityOptions([{ label: cityUf, value: cityUf }]);
          setIsCityDisabled(true);
          setCepWarning("");
        }
      } catch (error) {
        setIsCityDisabled(false);
        setValue("city", "");
        setCityOptions([]);
        setCepWarning(
          "CEP inválido - informe nome da cidade ou UF para pesquisa",
        );
      }
    };

    fetchCep();
  }, [zipcode, setValue, setSelected]);

  const handleCitySearch = async (search: string) => {
    if (!search) return;

    try {
      const cities = await crefazService.getCities(search);
      setCityOptions(
        (cities || []).map((city) => ({ label: city, value: city })),
      );
    } catch (error) {
      setCityOptions([]);
    }
  };

  useEffect(() => {
    if (!debouncedCityInput) return;

    handleCitySearch(debouncedCityInput);
  }, [debouncedCityInput]);

  useEffect(() => {
    if (!isSuccess || !data) return;

    if (data.name) {
      setValue("name", data.name);
    }

    if (data.birthDate) {
      setValue("birthDate", formatDateForInput(data.birthDate));
    }

    if (data.address?.zipCode) {
      setValue("zipcode", data.address.zipCode);
    }
  }, [isSuccess, data, setValue]);

  const handleOpenConfirmation = () => {
    setIsOpenConfirmModal(true);
  };

  const occupationOptions =
    occupationsData?.items.map((occupation) => ({
      label: occupation.nome,
      value: occupation.occupationId,
    })) ?? [];

  const submitModal = handleSubmit(
    async (formData: getGenerateProposalFormData) => {
      setIsOpenConfirmModal(false);
      setIsLoading(true);
      journeyStore.start(PartnerType.CREFAZ);

      const pinJoin = pin ? pin.join("") : "";

      const [city, state] = formData.city
        ? formData.city.split(" - ")
        : ["", ""];

      const payload = {
        nome: formData.name,
        cpf: formData.cpf.replace(/\D/g, ""),
        nascimento: formData.birthDate.split("/").reverse().join("-"),
        telefone: formData?.phone?.replace(/\D/g, "") || undefined,
        ocupacaoId: Number(formData.ocupation),
        cep: formData.zipcode?.replace(/\D/g, "") ?? "",
        city,
        state,
        pin: pinJoin || undefined,
      };

      const customerData = {
        name: payload.nome,
        cpf: payload.cpf,
        birthDate: payload.nascimento,
        phone: payload.telefone,
        ocupacaoId: String(payload.ocupacaoId),
        address: {
          zipCode: payload.cep,
          state,
          city,
        },
      };

      try {
        const customerContextData = {
          ...customerData,
        };

        setCustomerData(customerContextData);
      } catch (error: any) {
        toast({
          title: "Erro ao criar cliente",
          description: error?.message || "Não foi possível criar o cliente.",
          variant: "destructive",
        });

        setIsLoading(false);
        return;
      }

      try {
        const response = await makeProposalMutation.mutateAsync(payload);

        if (response?.propostaId) {
          setProposalData({
            propostaId: response.propostaId,
          });

          onContinue?.();
        }
      } finally {
        setIsLoading(false);
      }
    },
  );

  useEffect(() => {
    localStorage.removeItem("crefaz_customer_data");
  }, []);

  useRegisterSupportContext("simulation.crefaz.generate", {
    data: getGenerateProposalForm.watch(),
  });

  return {
    form: getGenerateProposalForm,
    selected,
    setSelected,
    isCityDisabled,
    occupationOptions,
    submitModal,
    isLoading,
    isOpenConfirmModal,
    setIsOpenConfirmModal,
    pin,
    setPin,
    handleOpenConfirmation,
    user,
    cityOptions,
    setCityInput,
    cepWarning,
  };
}
